import type { ProductKnowledgeScopeProduct, ProductKnowledgeScopeMember } from './types';
import { getScopeSpuKey, getScopeSkuKey } from './scope';

// UI identity includes the store and parent product; API keys retain the backend contract.
export const productSelectionKey = (product: ProductKnowledgeScopeProduct) =>
  JSON.stringify([product.platform, product.region, product.shopId, product.spuId]);
export const skuSelectionKey = (product: ProductKnowledgeScopeProduct, skuId: string) =>
  JSON.stringify([productSelectionKey(product), skuId]);
export const selectableKeys = (product: ProductKnowledgeScopeProduct) =>
  product.skus.length
    ? product.skus.map((sku) => skuSelectionKey(product, sku.skuId))
    : [productSelectionKey(product)];

export function mergeSelectionProducts(
  current: ProductKnowledgeScopeProduct[],
  incoming: ProductKnowledgeScopeProduct[],
) {
  const products = new Map(current.map((product) => [productSelectionKey(product), product]));
  incoming.forEach((product) => {
    const previous = products.get(productSelectionKey(product));
    const skus = new Map(previous?.skus.map((sku) => [sku.skuId, sku]));
    product.skus.forEach((sku) => skus.set(sku.skuId, sku));
    products.set(productSelectionKey(product), { ...product, skus: [...skus.values()] });
  });
  return [...products.values()];
}

export function selectedMembers(
  products: ProductKnowledgeScopeProduct[],
  selected: Set<string>,
): ProductKnowledgeScopeMember[] {
  const members: ProductKnowledgeScopeMember[] = [];
  products.forEach((product) => {
    const spuKey = getScopeSpuKey(product);
    if (!product.skus.length) {
      if (selected.has(productSelectionKey(product))) {
        members.push({ memberType: 'SPU', spuKey, skuKeys: [] });
      }
      return;
    }
    const skuKeys = product.skus
      .filter((sku) => selected.has(skuSelectionKey(product, sku.skuId)))
      .map((sku) => getScopeSkuKey(product, sku.skuId));
    if (skuKeys.length) members.push({ memberType: 'SKU', spuKey, skuKeys });
  });
  return members;
}
